import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Loader2, ListTodo, FileText, Search, Bell,
  Wallet, Repeat, ExternalLink, FolderKanban
} from "lucide-react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NEXO-CONSULTOR Etapa 13 — drawer 360° por projeto.
// Consome GET /api/projects/{id}/related: tasks, project_tasks, notes,
// research, reminders, transactions, subscriptions, totals.

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const SectionHeader = ({ icon: Icon, label, count }) => (
  <div className="flex items-center justify-between mb-2 mt-4">
    <div className="flex items-center gap-2 text-slate-200 text-sm font-medium">
      <Icon className="w-4 h-4 text-brand-cyan" />
      <span>{label}</span>
    </div>
    {count !== undefined && (
      <span className="text-xs text-slate-500">{count}</span>
    )}
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-xs text-slate-500 italic px-2 py-1.5">{label}</div>
);

const Row = ({ children }) => (
  <div className="text-sm text-slate-300 px-2 py-1.5 border-l-2 border-nectar-border hover:border-brand-cyan/40 hover:bg-white/5 rounded-r">
    {children}
  </div>
);

export default function ProjectDetailPanel({ projectId, isOpen, onClose }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRelated = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_URL}/api/projects/${projectId}/related`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || "Falha ao carregar projeto");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) fetchRelated();
  }, [isOpen, projectId, fetchRelated]);

  if (!isOpen) return null;

  const project = data?.project;
  const totals = data?.totals || {};

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 280 }}
        className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-nectar-bg border-l border-nectar-border z-50 flex flex-col"
        data-testid="project-detail-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-nectar-border">
          <div className="flex items-center gap-2 min-w-0">
            <FolderKanban className="w-5 h-5 text-brand-cyan flex-shrink-0" />
            <h2 className="text-lg font-semibold text-white truncate">
              {project?.title || "Projeto"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            data-testid="project-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-brand-cyan" />
            </div>
          )}

          {error && (
            <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
              {error}
            </div>
          )}

          {!isLoading && !error && data && (
            <>
              {project?.description && (
                <p className="text-sm text-slate-400 mb-3">{project.description}</p>
              )}

              {/* Totais financeiros — só mostra se tem alguma transação */}
              {(totals.transactions || 0) > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-nectar-card border border-nectar-border rounded-lg p-2">
                    <div className="text-xs text-slate-500">Despesas</div>
                    <div className="text-sm font-semibold text-rose-300">
                      {fmtBRL(totals.expenses_brl)}
                    </div>
                  </div>
                  <div className="bg-nectar-card border border-nectar-border rounded-lg p-2">
                    <div className="text-xs text-slate-500">Receitas</div>
                    <div className="text-sm font-semibold text-emerald-300">
                      {fmtBRL(totals.income_brl)}
                    </div>
                  </div>
                  <div className="bg-nectar-card border border-nectar-border rounded-lg p-2">
                    <div className="text-xs text-slate-500">Saldo</div>
                    <div className={`text-sm font-semibold ${(totals.balance_brl || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {fmtBRL(totals.balance_brl)}
                    </div>
                  </div>
                </div>
              )}

              <SectionHeader icon={ListTodo} label="Tarefas" count={totals.tasks} />
              {data.tasks?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.tasks.slice(0, 30).map((t) => (
                    <Row key={t.id}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={t.is_completed ? "line-through opacity-60" : ""}>
                          {t.title}
                        </span>
                        {t.priority === "high" && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300">
                            alta
                          </span>
                        )}
                      </div>
                    </Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem tarefas vinculadas" />
              )}

              <SectionHeader icon={Bell} label="Lembretes" count={totals.reminders} />
              {data.reminders?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.reminders.slice(0, 20).map((r) => (
                    <Row key={r.id}>{r.message}</Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem lembretes" />
              )}

              <SectionHeader icon={FileText} label="Notas" count={totals.notes} />
              {data.notes?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.notes.slice(0, 20).map((n) => (
                    <Row key={n.id}>
                      <div className="text-slate-200 font-medium">{n.title || "Nota"}</div>
                      <div className="text-xs text-slate-400 line-clamp-2">{n.content}</div>
                    </Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem notas vinculadas" />
              )}

              <SectionHeader icon={Search} label="Pesquisas" count={totals.research} />
              {data.research?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.research.slice(0, 10).map((n) => (
                    <Row key={n.id}>
                      <div className="text-slate-200 font-medium flex items-center gap-1">
                        {n.title || "Pesquisa"}
                        <ExternalLink className="w-3 h-3 text-slate-500" />
                      </div>
                      <div className="text-xs text-slate-400 line-clamp-3 whitespace-pre-line">{n.content}</div>
                    </Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem pesquisas" />
              )}

              <SectionHeader icon={Wallet} label="Transações" count={totals.transactions} />
              {data.transactions?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.transactions.slice(0, 30).map((t) => (
                    <Row key={t.id}>
                      <div className="flex items-center justify-between gap-2">
                        <span>{t.description}</span>
                        <span className={t.type === "expense" ? "text-rose-300" : "text-emerald-300"}>
                          {t.type === "expense" ? "-" : "+"}{fmtBRL(t.amount)}
                        </span>
                      </div>
                    </Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem transações vinculadas" />
              )}

              <SectionHeader icon={Repeat} label="Assinaturas" count={totals.subscriptions} />
              {data.subscriptions?.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {data.subscriptions.slice(0, 20).map((s) => (
                    <Row key={s.id}>
                      <div className="flex items-center justify-between gap-2">
                        <span>{s.name}</span>
                        <span className="text-slate-400">
                          {fmtBRL(s.amount)} / {s.frequency}
                        </span>
                      </div>
                    </Row>
                  ))}
                </div>
              ) : (
                <EmptyState label="Sem assinaturas vinculadas" />
              )}
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
